<template>
  <div class="flex flex-col items-center justify-center p-4">
    <div class="card w-full max-w-md bg-base-100 shadow-xl">
      <div class="card-body items-center text-center">
        <h2 class="card-title text-2xl mb-2">QR Code Scanner</h2>
        <p class="text-base-content/70 mb-6">Point your camera at a QR code to scan it.</p>
        
        <div class="w-full aspect-square bg-black rounded-box overflow-hidden relative shadow-inner border border-base-300 flex items-center justify-center">
          <QrcodeStream 
            class="w-full h-full object-cover" 
            @detect="onDetect" 
            @error="onError"
          />
        </div>

        <div class="mt-6 w-full space-y-3">
          
          <div v-if="scannedResult" class="alert alert-success shadow-sm">
            <NuxtLink :to="scannedResult">
            <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            <div class="text-left w-full overflow-hidden">
              <h3 class="font-bold">Scanned Successfully!</h3>
                <p class="text-sm break-all mt-1">{{ scannedResult }}</p>
            </div>
            </NuxtLink>
          </div>

          <div v-else-if="!errorMessage" class="alert alert-info shadow-sm">
            <span class="loading loading-dots loading-sm"/>
            <span>Waiting for QR code...</span>
          </div>

          <div v-if="errorMessage" class="alert alert-error shadow-sm text-left">
            <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            <span class="text-sm">{{ errorMessage }}</span>
          </div>
        </div>
        
        <div v-if="scannedResult" class="card-actions mt-4 w-full">
          <button class="btn btn-primary btn-block" @click="resetScanner">Scan Another</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import type { DetectedBarcode } from 'nuxt-qrcode'
// No need to import navigateTo, it is auto-imported by Nuxt

const scannedResult = ref<string | null>(null)
const errorMessage = ref<string | null>(null)

// Function to play the sound
function playSuccessSound() {
  try {
    const audio = new Audio('/sounds/beep.mp3') 
    audio.play().catch(err => {
      console.warn('Audio playback was prevented by the browser:', err)
    })
  } catch (err) {
    console.error('Audio initialization failed:', err)
  }
}

async function onDetect(detectedCodes: DetectedBarcode[]) {
  if (detectedCodes && detectedCodes.length > 0) {
    // 1. Extract the raw value
    const scannedUrl = detectedCodes[0].rawValue
    scannedResult.value = scannedUrl
    errorMessage.value = null
    // 2. Play the sound
    playSuccessSound()
    // 3. Parse and Redirect
    try {
      // Create a URL object to break down the scanned string
      const parsedUrl = new URL(scannedUrl)
      // Combine the path (e.g., '/') and the query string (e.g., '?shopId=1&tableId=1')
      const internalPath = parsedUrl.pathname + parsedUrl.search
      // Wait 500ms so the beep plays and the success UI flashes
      setTimeout(async () => {
        // Because it is now a relative path, we remove { external: true }
        // This gives you a smooth, instant Nuxt SPA transition!
        await navigateTo(internalPath)
      }, 200)
      
    } catch (err) {
      // If the QR code contains plain text instead of a valid link, 'new URL()' will fail.
      errorMessage.value = "Invalid QR code format. Please scan a valid table QR."
      console.error("Failed to parse or navigate:", err)
    }
  }
}

function onError(err: Error) {
  if (err.name === 'NotAllowedError') {
    errorMessage.value = 'Camera access denied. Please grant permissions.'
  } else if (err.name === 'NotFoundError') {
    errorMessage.value = 'No camera found on this device.'
  } else if (err.name === 'NotSupportedError') {
    errorMessage.value = 'Secure context required (HTTPS or localhost).'
  } else if (err.name === 'NotReadableError') {
    errorMessage.value = 'Camera is already in use by another application.'
  } else if (err.name === 'OverconstrainedError') {
    errorMessage.value = 'Requested camera is not suitable.'
  } else if (err.name === 'StreamApiNotSupportedError') {
    errorMessage.value = 'Browser does not support camera streaming.'
  } else {
    errorMessage.value = `Camera error: ${err.message}`
  }
}

function resetScanner() {
  scannedResult.value = null
  errorMessage.value = null
}
</script>